import React, { useState, useEffect, type ChangeEvent, type FormEvent } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Send, Mail, MessageSquare, HeadphonesIcon } from "lucide-react";
import { RootLayout } from "@/components/layout/RootLayout";
import { toast } from "@/components/ui/use-toast";
import { useAuth } from "@/context/AuthContext";
import { TurnstileWidget } from "@/components/auth/TurnstileWidget";
import {
  AUTH_CAPTCHA_CONTACT_HINT,
  AUTH_CAPTCHA_EXPIRED_ERROR,
  AUTH_CAPTCHA_HINT_CLASS,
  AUTH_CAPTCHA_REQUIRED_ERROR,
} from "@/lib/authCaptcha";
import { validateContactFormFields, type FieldErrors } from "@/utils/inlineFormValidation";
import { FieldInlineError } from "@/components/ui/fieldInlineError";
import { clearFieldError, fieldLabelClass } from "@/utils/fieldInlineErrorUtils";
import {
  getContactFormDefaultsFromUser,
  mergeContactFormDefaults,
} from "@/utils/contactFormDefaults";

/**
 * Contact us page component that allows users to send inquiries via a contact form.
 * @example
 * ContactUs()
 * <ContactUs />
 * @returns {JSX.Element} The rendered contact form page component.
 * @description
 *   - Simulates form submission with a delay to mimic real-world request-response cycle.
 *   - Utilizes toast notifications to inform users about form submission status.
 *   - Resets form data after successful submission.
 *   - Handles user input changes dynamically to update form state.
 */
export default function ContactUs() {
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaError, setCaptchaError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  useEffect(() => {
    if (!user) {
      return;
    }
    const defaults = getContactFormDefaultsFromUser(user);
    setFormData((prev) => mergeContactFormDefaults(prev, defaults));
  }, [user]);

  // Get Turnstile site key from environment
  // Check window.__ENV__ first (runtime config from docker-entrypoint.sh), then fallback to build-time config
  const envConfig = globalThis as typeof globalThis & {
    __ENV__?: { VITE_TURNSTILE_SITE_KEY?: string };
  };
  const turnstileSiteKey =
    envConfig.__ENV__?.VITE_TURNSTILE_SITE_KEY ?? import.meta.env.VITE_TURNSTILE_SITE_KEY ?? "";

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
    setFieldErrors((prev) => clearFieldError(prev, id));
  };

  /**
   * Handles the form submission process
   * @example
   * handleSubmit(event)
   * Form submission with success message toast
   * @param {Event} e - Event representing the form submission triggered by user.
   * @returns {void} No return value is produced.
   * @description
   *   - Prevents the default form submission behavior.
   *   - Sends email via notification service using Resend.
   *   - Displays a toast notification on successful submission.
   *   - Resets form fields to their initial empty state.
   */
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const errors = validateContactFormFields(formData);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    // Require CAPTCHA when configured
    if (turnstileSiteKey && !captchaToken) {
      setCaptchaError(AUTH_CAPTCHA_REQUIRED_ERROR);
      toast({
        title: "Verification required",
        description: "Please complete the CAPTCHA verification before sending your message.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const API_URL = import.meta.env.VITE_API_URL || "https://tellerwand.com";

      // Send structured data to notification-service (template rendered server-side)
      const response = await fetch(`${API_URL}/api/send-contact-form`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message,
          captchaToken: captchaToken || undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: "Failed to send email" }));
        throw new Error(errorData.message || "Failed to send email");
      }

      const result = await response.json();
      if (result.success) {
        toast({
          title: "Message sent",
          description: "We've received your message and will get back to you soon.",
        });
        setFormData({ name: "", email: "", message: "" });
        setFieldErrors({});
        setCaptchaToken(null);
        setCaptchaError(null);
      } else {
        throw new Error(result.message || "Failed to send email");
      }
    } catch (error) {
      console.error("Error sending contact form:", error);
      toast({
        title: "Failed to send message",
        description:
          error instanceof Error
            ? error.message
            : "Please try again later or email us directly at support@tellerwand.com",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCaptchaVerify = (token: string) => {
    setCaptchaToken(token);
    setCaptchaError(null);
  };

  const handleCaptchaError = (error: string) => {
    // Filter out expected 401/challenge-platform errors - these are normal Turnstile behavior
    if (
      error &&
      (error.includes("401") ||
        error.includes("challenge-platform") ||
        error.includes("PrivateToken") ||
        error.toLowerCase().includes("unauthorized"))
    ) {
      return;
    }
    setCaptchaError(error);
    setCaptchaToken(null);
  };

  const handleCaptchaExpire = () => {
    setCaptchaToken(null);
    setCaptchaError(AUTH_CAPTCHA_EXPIRED_ERROR);
  };

  const turnstileSection = turnstileSiteKey ? (
    <div className="space-y-2">
      <p className={AUTH_CAPTCHA_HINT_CLASS}>{AUTH_CAPTCHA_CONTACT_HINT}</p>
      <TurnstileWidget
        siteKey={turnstileSiteKey}
        onVerify={handleCaptchaVerify}
        onError={handleCaptchaError}
        onExpire={handleCaptchaExpire}
      />
      {captchaError && <p className="text-xs text-red-600">{captchaError}</p>}
    </div>
  ) : null;

  if (user) {
    return (
      <RootLayout>
        <Helmet>
          <title>Contact Us | TellerWand Support</title>
          <meta
            name="description"
            content="Get in touch with TellerWand support team. We're here to help with questions, feedback, or technical assistance."
          />
        </Helmet>

        <div className="container max-w-4xl mx-auto p-6">
          <Button variant="outline" asChild className="mb-6">
            <Link to="/dashboard" className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" /> Back to Dashboard
            </Link>
          </Button>

          <Card>
            <CardHeader>
              <h1 className="text-3xl font-bold mb-2">Contact Support</h1>
              <CardTitle className="text-xl text-muted-foreground">
                We're here to help you
              </CardTitle>
            </CardHeader>
            <CardContent className="prose">
              <p>
                Have questions, feedback, or need assistance? Our support team is ready to help you
                with any inquiries about TellerWand.
              </p>

              <div className="grid md:grid-cols-2 gap-8 my-6">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
                      <Mail className="w-5 h-5 text-orange-600" />
                      Email Support
                    </h3>
                    <p className="text-sm text-muted-foreground mb-2">
                      Our primary support channel for all inquiries.
                    </p>
                    <a
                      href="mailto:support@tellerwand.com"
                      className="text-orange-600 hover:text-orange-700 font-medium"
                    >
                      support@tellerwand.com
                    </a>
                    <p className="text-xs text-muted-foreground mt-2">
                      Response time: Within 24 hours for free users, within 4 hours for premium
                      users
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-orange-600" />
                      What We Help With
                    </h3>
                    <ul className="text-sm space-y-2 text-muted-foreground">
                      <li>• Technical support and troubleshooting</li>
                      <li>• Account and billing questions</li>
                      <li>• Feature requests and feedback</li>
                      <li>• Data import/export assistance</li>
                      <li>• Security and privacy inquiries</li>
                      <li>• Partnership opportunities</li>
                    </ul>
                  </div>

                  <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                    <h3 className="text-sm font-medium mb-2 text-orange-900">Quick Help</h3>
                    <p className="text-xs text-orange-700 mb-3">
                      Check out our FAQ page for instant answers to common questions.
                    </p>
                    <Button variant="outline" size="sm" asChild className="w-full">
                      <Link to="/faq">Visit FAQ</Link>
                    </Button>
                  </div>
                </div>

                <div>
                  <form className="space-y-4" onSubmit={handleSubmit} noValidate>
                    <ContactMessageFormFields
                      formData={formData}
                      fieldErrors={fieldErrors}
                      onChange={handleChange}
                      variant="dashboard"
                    />
                    {turnstileSection}
                    <Button
                      type="submit"
                      className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Sending..." : "Send Message"}
                      {!isSubmitting && <Send className="ml-2 h-4 w-4" />}
                    </Button>
                    <p className="text-xs text-center text-muted-foreground">
                      We typically respond within 24 hours
                    </p>
                  </form>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </RootLayout>
    );
  }

  return (
    <>
      <Helmet>
        <title>Contact Us | TellerWand - Get Support & Assistance</title>
        <meta
          name="description"
          content="Contact TellerWand support team for help with personal finance management, technical support, feature requests, or partnership inquiries. Email us at support@tellerwand.com."
        />
        <meta
          name="keywords"
          content="contact TellerWand, TellerWand support, customer service, financial software help, technical support, TellerWand email"
        />

        {/* Open Graph */}
        <meta property="og:title" content="Contact TellerWand Support Team" />
        <meta
          property="og:description"
          content="Get in touch with our support team for help, feedback, or inquiries. We respond within 24 hours."
        />
        <meta property="og:url" content="https://tellerwand.com/contact-us" />
        <meta property="og:type" content="website" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: "Contact TellerWand",
            description: "Contact TellerWand support for assistance",
            url: "https://tellerwand.com/contact-us",
            mainEntity: {
              "@type": "Organization",
              name: "TellerWand",
              email: "support@tellerwand.com",
              contactPoint: {
                "@type": "ContactPoint",
                contactType: "Customer Support",
                email: "support@tellerwand.com",
                availableLanguage: "English",
              },
            },
          })}
        </script>
      </Helmet>

      <RootLayout>
        <div className="bg-gradient-to-br from-orange-50 via-white to-orange-100">
          {/* Hero Section */}
          <section className="py-16 px-4 bg-white">
            <div className="container mx-auto text-center max-w-3xl">
              <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-orange-600 to-orange-500 bg-clip-text text-transparent">
                We're Here to Help
              </h1>
              <p className="text-xl text-gray-600">
                Have questions about TellerWand? Our support team is ready to assist you.
              </p>
            </div>
          </section>

          {/* Main Content */}
          <section className="py-12 px-4">
            <div className="container max-w-5xl mx-auto">
              <div className="grid md:grid-cols-2 gap-8">
                {/* Contact Information */}
                <div className="space-y-6">
                  <Card className="border-orange-200 shadow-sm">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Mail className="w-5 h-5 text-orange-600" />
                        Email Support
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 mb-3">
                        Our primary support channel. We respond to all inquiries promptly.
                      </p>
                      <a
                        href="mailto:support@tellerwand.com"
                        className="text-lg font-semibold text-orange-600 hover:text-orange-700 block"
                      >
                        support@tellerwand.com
                      </a>
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-xs text-gray-600">
                          <strong>Response Time:</strong>
                        </p>
                        <p className="text-xs text-gray-500 mt-1">• Free users: Within 24 hours</p>
                        <p className="text-xs text-gray-500">• Premium users: Within 4 hours</p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-orange-200 shadow-sm">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <HeadphonesIcon className="w-5 h-5 text-orange-600" />
                        What We Can Help With
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2.5 text-sm text-gray-600">
                        <li className="flex items-start gap-2">
                          <span className="text-orange-600 font-bold mt-0.5">✓</span>
                          <span>Technical support and troubleshooting</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-orange-600 font-bold mt-0.5">✓</span>
                          <span>Account setup and configuration</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-orange-600 font-bold mt-0.5">✓</span>
                          <span>Feature requests and product feedback</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-orange-600 font-bold mt-0.5">✓</span>
                          <span>Data import, export, and migration help</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-orange-600 font-bold mt-0.5">✓</span>
                          <span>Security and privacy questions</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-orange-600 font-bold mt-0.5">✓</span>
                          <span>Business partnerships and integrations</span>
                        </li>
                      </ul>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200 shadow-sm">
                    <CardHeader>
                      <CardTitle className="text-orange-900">Need Quick Answers?</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <Link
                          to="/faq"
                          className="text-orange-600 hover:text-orange-700 font-medium text-sm flex items-center gap-2"
                        >
                          <span>→</span> Visit our FAQ Page
                        </Link>
                        <p className="text-xs text-gray-600 mt-1 ml-5">
                          Find instant answers to common questions
                        </p>
                      </div>
                      <div>
                        <Link
                          to="/features"
                          className="text-orange-600 hover:text-orange-700 font-medium text-sm flex items-center gap-2"
                        >
                          <span>→</span> Explore Features
                        </Link>
                        <p className="text-xs text-gray-600 mt-1 ml-5">
                          Learn about all TellerWand capabilities
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Contact Form */}
                <div>
                  <Card className="shadow-lg">
                    <CardHeader>
                      <CardTitle className="text-2xl">Send Us a Message</CardTitle>
                      <p className="text-sm text-gray-600">
                        Fill out the form below and we'll get back to you as soon as possible.
                      </p>
                    </CardHeader>
                    <CardContent>
                      <form className="space-y-4" onSubmit={handleSubmit} noValidate>
                        <ContactMessageFormFields
                          formData={formData}
                          fieldErrors={fieldErrors}
                          onChange={handleChange}
                          variant="public"
                        />
                        {turnstileSection}

                        <Button
                          type="submit"
                          className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                          size="lg"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <span className="animate-pulse">Sending...</span>
                          ) : (
                            <>
                              Send Message
                              <Send className="ml-2 h-4 w-4" />
                            </>
                          )}
                        </Button>

                        <p className="text-xs text-center text-gray-500">
                          We'll respond to your inquiry within 24 hours
                        </p>
                      </form>
                    </CardContent>
                  </Card>

                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-900">
                      <strong>Pro Tip:</strong> For faster resolution of technical issues, please
                      include your registered email address and a detailed description of the
                      problem.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </RootLayout>
    </>
  );
}

interface ContactMessageFormFieldsProps {
  formData: { name: string; email: string; message: string };
  fieldErrors: FieldErrors;
  onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  variant: "dashboard" | "public";
}

function ContactMessageFormFields({
  formData,
  fieldErrors,
  onChange,
  variant,
}: Readonly<ContactMessageFormFieldsProps>) {
  const isPublic = variant === "public";
  const inputClass = isPublic
    ? "w-full rounded-lg border border-gray-300 p-2.5 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition"
    : "w-full rounded border p-2";
  const textareaClass = isPublic ? `${inputClass} resize-none` : inputClass;
  const labelClass = (hasError: boolean) =>
    isPublic
      ? `block font-medium text-sm mb-1.5 ${fieldLabelClass(hasError)}`
      : `block font-medium text-sm mb-1 ${fieldLabelClass(hasError)}`;

  return (
    <>
      <div>
        <label className={labelClass(Boolean(fieldErrors.name))} htmlFor="name">
          {isPublic ? (
            <>
              Your Name <span className="text-red-500">*</span>
            </>
          ) : (
            "Name"
          )}
        </label>
        <input
          id="name"
          name="name"
          type="text"
          value={formData.name}
          onChange={onChange}
          className={inputClass}
          placeholder={isPublic ? "John Doe" : "Your full name"}
          aria-invalid={Boolean(fieldErrors.name)}
        />
        <FieldInlineError message={fieldErrors.name} />
      </div>
      <div>
        <label className={labelClass(Boolean(fieldErrors.email))} htmlFor="email">
          {isPublic ? (
            <>
              Email Address <span className="text-red-500">*</span>
            </>
          ) : (
            "Email"
          )}
        </label>
        <input
          id="email"
          name="email"
          type="email"
          value={formData.email}
          onChange={onChange}
          className={inputClass}
          placeholder={isPublic ? "john@example.com" : "your.email@example.com"}
          aria-invalid={Boolean(fieldErrors.email)}
        />
        <FieldInlineError message={fieldErrors.email} />
      </div>
      <div>
        <label className={labelClass(Boolean(fieldErrors.message))} htmlFor="message">
          {isPublic ? (
            <>
              Your Message <span className="text-red-500">*</span>
            </>
          ) : (
            "Message"
          )}
        </label>
        <textarea
          id="message"
          name="message"
          rows={isPublic ? 6 : 5}
          value={formData.message}
          onChange={onChange}
          className={textareaClass}
          placeholder={
            isPublic
              ? "How can we help you? Please provide as much detail as possible..."
              : "Tell us how we can help you..."
          }
          aria-invalid={Boolean(fieldErrors.message)}
        />
        {isPublic && (
          <p className="text-xs text-gray-500 mt-1.5">
            Please include any relevant details about your inquiry
          </p>
        )}
        <FieldInlineError message={fieldErrors.message} />
      </div>
    </>
  );
}
